import { faChevronRight, faCircleNotch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, ListGroup, Modal, ProgressBar } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { Topbar } from '../../components/Topbar/Topbar';
import { CloudFunctions, download, formatSecondsTimestamp, getFunctionsUrl, parseDuration } from '../../utils/utils';
import './Course.scss';
import { useTranslation } from 'react-i18next';
import { Title } from '../../components/Title/Title';

const CourseComponent = () => {
  const { getCourse, courses, subscription, currentUser, userDataFmt } = useFirebase();
  const { id } = useParams<any>();
  const history = useHistory();
  const [course, setCourse] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [courseUnlocked, setCourseUnlocked] = useState(false);

  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  const [isTestDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);

  const { t } = useTranslation();

  const getStatus = (bool: boolean, status: string, className = '') => (
    <Badge pill variant={bool ? 'success' : 'danger'} className={className}>
      {t(status)}
    </Badge>
  );

  const unitCompleted = useMemo(() => {
    if (!course) return 0;
    return course.chapters.flatMap((chapter) => chapter.units.filter((unit) => unit.completed)).length;
  }, [course]);

  const units = useMemo(() => {
    if (!course) return 0;
    return course.chapters.flatMap((chapter) => chapter.units.flatMap((unit) => unit)).length;
  }, [course]);

  useEffect(() => {
    if (!id) history.goBack();
  }, []);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const course = await getCourse(id);
      if (!course) return;
      setCourse(course);
    })();
  }, [id, courses]);

  useEffect(() => {
    if (!userDataFmt) return;

    if (userDataFmt.admin) {
      setCourseUnlocked(true);
      return;
    }

    if (course && course.companies) {
      // The check that the user is in the company is done server-side by the cloud function
      setCourseUnlocked(true);
      return;
    }

    if (!subscription) return;
    try {
      if (subscription.items[0].price.product.metadata.UNLOCK_COURSES == 'yes') {
        setCourseUnlocked(true);
      }
    } catch (e) {
      console.error(e);
    }
  }, [subscription, userDataFmt, course]);

  const getUnitPercentages = (unit) => {
    const getPercentage = (total, partial) => ((partial / total) * 100) | 0;
    const totalTime = unit.content.duration;
    const minimumTime = unit.minimumTime * 1000;
    const presenceTime = (unit.content.timePresence ?? 0) * 1000;
    const presencePercentage = getPercentage(totalTime, presenceTime);
    const minimumPercentage = minimumTime ? Math.max(getPercentage(totalTime, minimumTime) - presencePercentage, 0) : 0;
    const hasLeft = presenceTime < minimumTime;
    return (
      <ProgressBar className={'unit-progress'}>
        <ProgressBar variant={hasLeft ? 'primary' : 'success'} now={presencePercentage} key={1} />
        {!!minimumTime && <ProgressBar variant={'warning'} now={minimumPercentage} key={2} />}
      </ProgressBar>
    );
  };

  if (!course) return <FullscreenLoader />;

  return (
    <>
      <Title>{course.name}</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'mb-5 mt-3 mt-sm-5'}>
        <div className={'d-flex flex-column align-items-start'}>
          <h1 className="mb-2 mobile-h1">{course.name}</h1>
        </div>
        {!!course.description && (
          <blockquote>
            {course.description.map((segment, i) => (
              <div key={i.toString()}>{segment}</div>
            ))}
          </blockquote>
        )}
        <div className={'d-flex align-items-center mt-sm-1 mb-3'} style={{ gap: '1rem' }}>
          {getStatus(course.completed, course.status)}
          <ProgressBar
            min={0}
            max={units}
            now={unitCompleted}
            style={{ width: '100%' }}
            label={`${unitCompleted}/${units}`}
            variant={unitCompleted === units ? 'success' : 'primary'}
          ></ProgressBar>
          {course.tests && course.tests.length > 0 && (
            <Button
              className={'btn-sm'}
              disabled={isFetching || unitCompleted !== units || !courseUnlocked}
              onClick={
                unitCompleted !== units || !courseUnlocked
                  ? null
                  : () => {
                      setTestDialogOpen(true);
                    }
              }
            >
              Test
            </Button>
          )}

          {course.withCertificate && (
            <Button
              variant={'primary'}
              onClick={
                !course.completed || !courseUnlocked
                  ? null
                  : async () => {
                      setIsFetching(true);
                      const response = await fetch(getFunctionsUrl(CloudFunctions.GenerateCertificationStream), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          courseId: course.id,
                          uid: currentUser.uid,
                          token: await currentUser.getIdToken(true),
                          companyId: course.companies?.[0]?.id,
                        }),
                      });
                      setIsFetching(false);
                      await download(response);
                    }
              }
              className={'mt-3 mx-auto mx-sm-0 mt-sm-0 btn-sm'}
              disabled={isFetching || !course.completed || !courseUnlocked}
            >
              {isFetching ? (
                <FontAwesomeIcon icon={faCircleNotch} spin />
              ) : (
                <span className={'text-nowrap'}>
                  {/* <FontAwesomeIcon icon={faDownload} className={'mr-2'} /> */}
                  {t('course.labels.scaricaAttestato')}
                </span>
              )}
            </Button>
          )}
        </div>

        {!!course.reasonNotAccessible && (
          <Card bg={'warning'}>
            <Card.Body>{course.reasonNotAccessible}</Card.Body>
          </Card>
        )}

        {/* {course.completed && courseUnlocked && (
          <Card bg={'dark'}>
            <Card.Body>
              <div className={'d-flex align-items-center justify-content-between flex-column flex-sm-row'}>
                <h3 className={'mb-0 mobile-h3 '}>{t('course.labels.ottieniAttestato')}</h3>
                <Button
                  variant={'success'}
                  onClick={async () => {
                    setIsFetching(true);
                    const response = await fetch(getFunctionsUrl(CloudFunctions.GenerateCertificationStream), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        courseId: course.id,
                        uid: currentUser.uid,
                        token: await currentUser.getIdToken(true),
                      }),
                    });
                    setIsFetching(false);
                    await download(response);
                  }}
                  className={'mt-3 mx-auto mx-sm-0 mt-sm-0'}
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                  ) : (
                    <span>
                      <FontAwesomeIcon icon={faDownload} className={'mr-2'} /> {t('course.labels.scaricaAttestato')}
                    </span>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )} */}

        {course.chapters.flatMap((chapter) => (
          <div className={'row'}>
            {chapter.units.flatMap((unit) => (
              <div className={'col-12 col-md-6 col-lg-4'}>
                <ListGroup
                  key={unit.id}
                  className={'course-card mt-3 ' + (!unit.accessible ? 'disabled' : '')}
                  style={{ backgroundImage: `url(${unit.content.sm_thumbnail})` }}
                >
                  <ListGroup.Item
                    onClick={() => (unit.accessible ? history.push(`/course/${id}/watch/${unit.content.id}`) : null)}
                    variant={'dark'}
                    className={'dark-list-item course-list-item p-0 border-0'}
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <div className={'d-flex flex-column flex-lg-grow mt-auto'}>
                      <div className={'d-flex flex-column-reverse flex-sm-row align-items-start justify-content-between w-100 mt-2'}>
                        {/*{!!unit.reasonNotAccessible && <small className={'text-muted mr-2'}>{unit.reasonNotAccessible}</small>}*/}
                        <div className={'d-flex mt-auto w-100 align-items-end'}></div>
                      </div>
                    </div>
                  </ListGroup.Item>
                </ListGroup>
                {getUnitPercentages(unit)}

                <div className={'d-flex bg-dark p-1'}>
                  {unit.required && (
                    <Badge className={'rounded-0'} variant={'secondary'}>
                      {t('course.labels.unitaObbligatoria')}
                    </Badge>
                  )}
                  {getStatus(unit.completed, unit.status, 'rounded-0')}
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={'mr-2 ml-auto'}
                    style={{ cursor: 'pointer' }}
                    onClick={(evt) => {
                      setSelectedUnit(unit);
                      setIsMoreInfoDialogOpen(true);
                      evt.stopPropagation();
                    }}
                  />
                  <Badge className={'rounded-0'} variant={'primary'}>
                    {Math.round(unit.content.duration / 60 / 1000).toFixed(0) + ' min'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ))}
      </Container>

      <Footer />

      {isMoreInfoDialogOpen && (
        <Modal
          show={isMoreInfoDialogOpen}
          onHide={() => {
            setIsMoreInfoDialogOpen(false);
            setSelectedUnit(null);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('course.labels.dettaglioiUnita')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h6>{selectedUnit.name}</h6>
            {!!selectedUnit.reasonNotAccessible && <Alert variant={'warning'}>{selectedUnit.reasonNotAccessible}</Alert>}
            {selectedUnit.description && selectedUnit.description.map((x) => <div key={x}>{x}</div>)}
            <div>
              {t('course.labels.durata')} <b>{parseDuration(selectedUnit.content.duration)}</b>
            </div>
            {!!selectedUnit.minimumTime && (
              <div>
                {t('course.labels.presenzaMinima')}
                <b>{parseDuration(selectedUnit.minimumTime * 1000)}</b>
              </div>
            )}
            <div>
              {t('course.labels.tempoPresenza')}
              <b>{parseDuration((selectedUnit.content.timePresence ?? 0) * 1000)}</b>
            </div>
            <div>
              {t('course.labels.accessi')} <b>{selectedUnit.openings || 0}</b>
            </div>
            {!!selectedUnit.lastOpened && (
              <div>
                {t('course.labels.ultimoAccesso')}
                <b>{formatSecondsTimestamp(selectedUnit.lastOpened._seconds, 'dd MMMM Y HH:mm')}</b>
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {isTestDialogOpen && (
        <Modal
          dialogClassName={'tests-dialog'}
          show={isTestDialogOpen}
          onHide={() => {
            setTestDialogOpen(false);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('course.labels.listaTest')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {
              <ListGroup>
                {course.tests.map((test) => (
                  <ListGroup.Item key={test.id} variant={'light'}>
                    <div className={'d-flex flex-column flex-sm-row align-items-start align-items-sm-center'}>
                      <h3 className={'list-group-h3 m-0 '}>{test.name}</h3>
                      {getStatus(test.completed, test.status, 'ml-0 mt-1 ml-sm-2 mt-sm-0')}
                    </div>

                    <div className={'d-flex flex-column-reverse flex-sm-row align-items-start justify-content-between w-100 mt-2'}>
                      <details className={'details-mobile'}>
                        <summary>{t('course.labels.dettaglioTest')}</summary>
                        <div>
                          {t('course.labels.domande')}
                          <b>{test.questions.length}</b>
                        </div>
                        <div>
                          {t('course.labels.tentativi')} <b>{test.attempts}</b>
                        </div>
                      </details>
                      <div className={'d-flex flex-column flex-sm-row align-items-start align-items-sm-center'}>
                        {!!test.reasonNotAccessible && <small className={'text-muted mr-2'}>{test.reasonNotAccessible}</small>}
                        <Button
                          className={'mt-2 mt-sm-0'}
                          disabled={!test.accessible}
                          variant={'primary'}
                          onClick={() => {
                            history.push(`/course/${id}/test/${test.id}`);
                          }}
                        >
                          <span>{t('course.labels.vaiAlTest')}</span>
                          <FontAwesomeIcon className={'ml-2'} size={'xs'} icon={faChevronRight} />
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            }
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default CourseComponent;
